import { useState, useRef, useEffect } from 'react'
import { Download, FileSpreadsheet, FileText, Loader2, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface ExportFormat {
  label: string
  format: string
  icon: React.ReactNode
}

interface ExportDropdownProps {
  formats: ExportFormat[]
  onExport: (format: string) => Promise<void>
  label?: string
}

export function ExportDropdown({ formats, onExport, label = 'Export' }: ExportDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [exporting, setExporting] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Determine alignment dynamically when opened
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const containerRect = dropdownRef.current.getBoundingClientRect()
      const popupWidth = 180 // matches min-w-[180px]
      
      let idealLeft = containerRect.left
      
      // Prevent overflowing right edge
      if (idealLeft + popupWidth > window.innerWidth - 16) {
        idealLeft = window.innerWidth - popupWidth - 16
      }
      
      // Prevent overflowing left edge
      if (idealLeft < 16) {
        idealLeft = 16
      }
      
      // Calculate relative offset from container
      setDropdownStyle({
        left: `${idealLeft - containerRect.left}px`,
      })
    }
  }, [isOpen])

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleExport = async (format: string) => {
    setExporting(format)
    setError(null)
    try {
      await onExport(format)
      setIsOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed')
    } finally {
      setExporting(null)
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border transition-colors',
          'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700',
          'text-neutral-700 dark:text-neutral-300',
          'hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:border-neutral-300 dark:hover:border-neutral-600',
        )}
      >
        <Download className="w-4 h-4" />
        {label}
        <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div 
          style={dropdownStyle}
          className="absolute top-full mt-1 z-50 bg-white dark:bg-neutral-900 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-800 py-1 min-w-[180px]"
        >
          {error && (
            <div className="px-3 py-2 text-xs text-red-600 dark:text-red-400 border-b border-neutral-200 dark:border-neutral-800">
              {error}
            </div>
          )}
          {formats.map((f) => (
            <button
              key={f.format}
              onClick={() => handleExport(f.format)}
              disabled={exporting !== null}
              className={cn(
                'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors',
                'text-neutral-700 dark:text-neutral-300',
                'hover:bg-neutral-50 dark:hover:bg-neutral-800',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              {exporting === f.format ? (
                <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
              ) : (
                f.icon
              )}
              {f.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Pre-built format configs for convenience
export const XLSX_FORMAT: ExportFormat = {
  label: 'Export as XLSX',
  format: 'xlsx',
  icon: <FileSpreadsheet className="w-4 h-4 text-green-600 dark:text-green-500" />,
}

export const PDF_FORMAT: ExportFormat = {
  label: 'Export as PDF',
  format: 'pdf',
  icon: <FileText className="w-4 h-4 text-red-600 dark:text-red-500" />,
}
